import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import StarIcon from '@mui/icons-material/Star'
import SearchIcon from '@mui/icons-material/Search'
import services from '../../services'
import { useAuth } from '../../hooks/useAuth'
import { useNovoPortal } from '../estado'
import { marcaDoAmbiente } from '../tema'
import IndicadorDoCard from './IndicadorDoCard'
import './inicio.scss'

interface DashboardInicio { dashboardId: number; ambienteId: number; nome: string; ambiente: string; tipo: string; quando?: string | null }
interface AmbienteInicio { id: number; nome: string; marcaId?: number | null; dashboards: number }
interface InicioDados {
  resumo: { ambientes: number; dashboards: number; dashboardsNovos: number }
  favoritos: DashboardInicio[]
  recentes: DashboardInicio[]
  ambientes: AmbienteInicio[]
}

function ha(iso?: string | null): string {
  if (!iso) return ''
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (min < 2) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.round(h / 24)
  return d === 1 ? 'ontem' : `há ${d} dias`
}

function saudacao(): string {
  const h = new Date().getHours()
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite'
}

const MOSTRAR_AMBIENTES = 8

export default function Inicio() {
  const est = useNovoPortal()
  const { getUser } = useAuth()
  const navigate = useNavigate()
  const usuario = getUser()
  const [dados, setDados] = useState<InicioDados | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [todos, setTodos] = useState(false)

  useEffect(() => {
    services.api.novoPortalInicio().then((r: any) => {
      if (r.fail) setErro(r.error || 'Não foi possível montar a página inicial agora.')
      else setDados(r.data)
    })
  }, [])

  const ambientes = useMemo(() => {
    if (!dados) return []
    const q = busca.trim().toLowerCase()
    return [...dados.ambientes]
      .filter(a => !q || a.nome.toLowerCase().includes(q) || marcaDoAmbiente(est.marcas, est.ambientes, a.id).nome.toLowerCase().includes(q))
      .sort((x, y) => x.nome.localeCompare(y.nome))
  }, [dados, busca, est.marcas, est.ambientes])

  const nome = (usuario?.fullName || usuario?.userName || '').split(' ')[0]
  const hoje = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })
  const abrir = (d: { dashboardId: number; ambienteId: number }) => navigate(`/app/dashboard/${d.dashboardId}/${d.ambienteId}`)
  const visiveis = todos || busca.trim() ? ambientes : ambientes.slice(0, MOSTRAR_AMBIENTES)

  const tiles = dados ? [
    { chave: 'amb', valor: dados.resumo.ambientes, texto: dados.resumo.ambientes === 1 ? 'ambiente que você acompanha' : 'ambientes que você acompanha' },
    { chave: 'dash', valor: dados.resumo.dashboards, texto: dados.resumo.dashboards === 1 ? 'dashboard que você acessa' : 'dashboards que você acessa' },
    { chave: 'fav', valor: dados.favoritos.length, texto: dados.favoritos.length === 1 ? 'favorito' : 'favoritos' },
    { chave: 'novos', valor: dados.resumo.dashboardsNovos, texto: dados.resumo.dashboardsNovos === 1 ? 'dashboard novo esta semana' : 'dashboards novos esta semana' }
  ] : []

  return (
    <div className="ni">
      <header className="ni-cab">
        <div>
          <h1>{saudacao()}{nome ? `, ${nome}` : ''}</h1>
          <p className="ni-data">{hoje.charAt(0).toUpperCase() + hoje.slice(1)}</p>
        </div>
        <button type="button" className="ni-buscar" onClick={() => window.dispatchEvent(new Event('np-abrir-busca'))}>
          <SearchIcon fontSize="small" /><span>Buscar dashboard</span><kbd>Ctrl K</kbd>
        </button>
      </header>

      {erro && <div className="ni-erro">{erro}</div>}
      {!dados && !erro && <div className="ni-carregando">Montando o resumo dos seus ambientes…</div>}

      {dados && (
        <>
          <section className="ni-resumo" aria-label="Resumo">
            {tiles.map(t => <div key={t.chave} className="ni-tile"><strong>{t.valor}</strong><span>{t.texto}</span></div>)}
          </section>

          <div className="ni-grade">
            <div className="ni-col">
              <section className="ni-cartao">
                <h2>Favoritos</h2>
                {dados.favoritos.length === 0
                  ? <p className="ni-vazio">Marque a estrela ao lado de um dashboard e ele aparece aqui, de qualquer ambiente.</p>
                  : <div className="ni-favs">
                    {dados.favoritos.map(f => {
                      return (
                        <button key={`${f.ambienteId}-${f.dashboardId}`} type="button" className="ni-fav" onClick={() => abrir(f)}>
                          <span className="ni-fav-linha"><StarIcon fontSize="inherit" /><span className="ni-tag">{marcaDoAmbiente(est.marcas, est.ambientes, f.ambienteId).nome}</span></span>
                          <b>{f.nome}</b>
                          <small>{f.ambiente}</small>
                          <IndicadorDoCard dashboardId={f.dashboardId} ambienteId={f.ambienteId} />
                        </button>
                      )
                    })}
                  </div>}
              </section>

              <section className="ni-cartao">
                <h2>Seus ambientes {!busca.trim() && ambientes.length > MOSTRAR_AMBIENTES &&
                  <button type="button" className="ni-mais" onClick={() => setTodos(!todos)}>{todos ? 'Ver menos' : `Ver os ${ambientes.length}`}</button>}</h2>
                <label className="ni-filtro"><SearchIcon fontSize="small" />
                  <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar ambiente ou marca" aria-label="Buscar ambiente ou marca" />
                </label>
                <div className="ni-tabela">
                  <table>
                    <thead><tr><th>Ambiente</th><th className="n">Dashboards</th></tr></thead>
                    <tbody>
                      {visiveis.map(a => {
                        return (
                          <tr key={a.id} onClick={() => navigate(`/app/dashboard/0/${a.id}`)}>
                            <td><span className="ni-amb"><span className="ni-amb-ini">{a.nome.charAt(0)}</span><span>{a.nome} <span className="ni-tag">{marcaDoAmbiente(est.marcas, est.ambientes, a.id).nome}</span></span></span></td>
                            <td className="n">{a.dashboards}</td>
                          </tr>
                        )
                      })}
                      {visiveis.length === 0 && <tr><td colSpan={2} className="ni-traco">Nenhum ambiente com esse nome.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </section>
            </div>

            <div className="ni-col">
              <section className="ni-cartao">
                <h2>Recentes</h2>
                {dados.recentes.length === 0
                  ? <p className="ni-vazio">Os dashboards que você abrir aparecem aqui.</p>
                  : <ul className="ni-recentes">
                    {dados.recentes.map(r => (
                      <li key={`${r.ambienteId}-${r.dashboardId}`}>
                        <Link to={`/app/dashboard/${r.dashboardId}/${r.ambienteId}`}><span><b>{r.nome}</b><small>{r.ambiente}</small></span><time>{ha(r.quando)}</time></Link>
                      </li>
                    ))}
                  </ul>}
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
